import React, { useState, useCallback } from 'react';
import { useMonetization } from '../../contexts/MonetizationContext';
import { useTranslation } from '../../contexts/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { trackEvent } from '../../utils/analytics';
import { gatherAdConsent } from '../../utils/adConsent';
import './RewardedAdButton.css';

/**
 * RewardedAdButton component for showing rewarded video ads.
 * When the ad is completed, the user receives a hint.
 *
 * Props:
 * - onRewardGranted: Callback when reward is granted
 * - onError: Callback when ad fails to load/show
 * - disabled: Whether the button is disabled
 */
function RewardedAdButton({ onRewardGranted, onError, disabled = false }) {
    const { grantAdReward, getAdChallenge, isNativeMobile } = useMonetization();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [adReady, setAdReady] = useState(false);

    const showRewardedAd = useCallback(async () => {
        setLoading(true);

        try {
            if (isNativeMobile()) {
                // Use AdMob for native platforms
                await showAdMobRewarded();
            } else {
                // Use Google AdSense rewarded for web (or simulate)
                await showWebRewarded();
            }
            // Track impression only after ad was actually shown
            trackEvent('Ad Impression', { ad_type: 'rewarded' });
        } catch (error) {
            console.error('Error showing rewarded ad:', error);
            onError?.(error.message);
        } finally {
            setLoading(false);
        }
    }, [isNativeMobile, onRewardGranted, onError, grantAdReward, getAdChallenge, t]);

    const showAdMobRewarded = async () => {
        // Check if AdMob plugin is available
        if (!window.Capacitor?.Plugins?.AdMob) {
            // Fallback: use web-style rewarded ad when AdMob plugin isn't loaded
            await showWebRewarded();
            return;
        }

        const { AdMob } = window.Capacitor.Plugins;
        const platform = window.Capacitor?.getPlatform?.() || 'android';

        // Get ad unit ID
        const adUnitId = platform === 'ios'
            ? (process.env.REACT_APP_ADMOB_REWARDED_ID_IOS || 'ca-app-pub-3940256099942544/1712485313') // Test ID
            : (process.env.REACT_APP_ADMOB_REWARDED_ID_ANDROID || 'ca-app-pub-3940256099942544/5224354917'); // Test ID

        // B17: a production build serving Google's TEST unit means the real
        // REACT_APP_ADMOB_REWARDED_ID_* vars weren't set at build time → $0
        // rewarded revenue. Warn loudly (surfaces in Sentry once configured).
        if (process.env.NODE_ENV === 'production' && adUnitId.includes('ca-app-pub-3940256099942544')) {
            console.warn('[AdMob] Using a TEST rewarded ad unit in a PRODUCTION build — set REACT_APP_ADMOB_REWARDED_ID_IOS/ANDROID to earn revenue.');
        }

        // Helper: race a promise against a timeout
        const withTimeout = (promise, ms) =>
            Promise.race([
                promise,
                new Promise((_, reject) => setTimeout(() => reject(new Error('AdMob timeout')), ms)),
            ]);

        const consent = await gatherAdConsent();
        if (consent?.canRequestAds === false) {
            await showWebRewarded();
            return;
        }

        try {
            // Prepare the rewarded ad (timeout after 10s)
            await withTimeout(
                AdMob.prepareRewardVideoAd({
                    adId: adUnitId,
                    isTesting: process.env.NODE_ENV === 'development',
                }),
                10000,
            );

            // Ad loaded successfully — show it
            try {
                await withTimeout(AdMob.showRewardVideoAd(), 60000);
            } catch (showErr) {
                // showRewardVideoAd rejects on dismiss even after reward on some platforms.
                // User already saw the ad, so this is fine — grant reward below.
                console.warn('AdMob show resolved with error (likely dismiss):', showErr);
            }

            // User saw the ad (it loaded and was shown) — always grant the reward
            await grantAdReward('rewarded_video', adUnitId);
            onRewardGranted?.();
        } catch (loadError) {
            // Ad failed to LOAD — fall back to web timer
            console.warn('AdMob ad failed to load, falling back to web:', loadError);
            await showWebRewarded();
        }
    };

    const showWebRewarded = async () => {
        // Fetch the server-issued challenge BEFORE showing the ad. The
        // server requires the redemption to come at least a few seconds
        // after the challenge was minted, which prevents direct curl-style
        // claims that skip the ad entirely. If we can't reach the server,
        // we abort rather than silently grant a reward.
        const challenge = await getAdChallenge();
        if (!challenge) {
            onError?.(t('ads.rewarded.startFailed'));
            return;
        }

        // Show a timed ad overlay — user waits to earn a free hint
        const confirmed = await new Promise((resolve) => {
            const overlay = document.createElement('div');
            overlay.className = 'rewarded-ad-overlay';
            overlay.innerHTML = `
                <div class="rewarded-ad-container">
                    <div class="rewarded-ad-content">
                        <h3>${t('ads.rewarded.title')}</h3>
                        <p>${t('ads.rewarded.wait')}</p>
                        <div class="rewarded-ad-timer">
                            <span class="timer-text">${t('common.loading')}</span>
                            <div class="timer-bar"></div>
                        </div>
                    </div>
                </div>
            `;

            document.body.appendChild(overlay);

            const duration = 5000;
            const timerBar = overlay.querySelector('.timer-bar');
            const timerText = overlay.querySelector('.timer-text');

            let elapsed = 0;
            const interval = setInterval(() => {
                elapsed += 100;
                const progress = (elapsed / duration) * 100;
                timerBar.style.width = `${progress}%`;

                const remaining = Math.ceil((duration - elapsed) / 1000);
                timerText.textContent = t('ads.rewarded.completingIn', { count: remaining });

                if (elapsed >= duration) {
                    clearInterval(interval);
                    overlay.remove();
                    resolve(true);
                }
            }, 100);
        });

        if (confirmed) {
            const result = await grantAdReward('rewarded_video', 'web-rewarded', challenge);
            if (result?.hintGranted) {
                onRewardGranted?.();
            } else {
                onError?.(result?.error || t('ads.rewarded.rewardFailed'));
            }
        }
    };

    return (
        <button
            className="rewarded-ad-button"
            onClick={showRewardedAd}
            disabled={disabled || loading}
        >
            {loading ? (
                <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>{t('ads.rewarded.loadingAd')}</span>
                </>
            ) : (
                <>
                    <FontAwesomeIcon icon={faPlay} />
                    <span>{t('ads.rewarded.watch')}</span>
                </>
            )}
        </button>
    );
}

export default RewardedAdButton;
